/**
 * Tracé détaillé à partir d'une réponse Google Directions.
 *
 * Historique : le tracé sauvegardé (`trkseg`) était `overview_path`,
 * la version volontairement simplifiée par Google. Sur les longs trajets
 * (plusieurs centaines de km), l'espacement médian entre points dépasse
 * 2 km : le rendu mobile dessine alors de grandes lignes droites qui
 * coupent les virages. La géométrie complète existe pourtant dans la
 * réponse, au niveau des pas (`legs[].steps[].path`) — c'est elle que
 * `DirectionsRenderer` affiche à l'écran.
 *
 * Ce module extrait cette géométrie complète. Le type de sortie reste
 * identique à `overview_path` (objets `LatLng` de Google) afin de ne pas
 * perturber les consommateurs en mémoire qui appellent `lat()`/`lng()`
 * (`GpxToServ`, `ExportProject`, `CompileProject`) ; la sérialisation JSON
 * vers le backend produit toujours `{lat, lng}` numériques (contrat
 * consommé par le mobile, cf. `Project.fromJson`).
 */

/**
 * Précision de stockage des coordonnées : 5 décimales (~1,1 m), alignée sur
 * le format polyline encodé (1e5). Réduit le volume du trkseg (~25 % sur un
 * long trajet) et supprime le bruit flottant (ex. 47.79748000000001).
 */
const COORDINATE_PRECISION = 1e5;

const roundCoordinate = (value) => Math.round(value * COORDINATE_PRECISION) / COORDINATE_PRECISION;

export const readTrackCoordinate = (point, key) =>
  typeof point[key] === "function" ? point[key]() : Number(point[key]);

/**
 * Vérifie qu'un point ressemble à un LatLng Google (méthodes lat()/lng()).
 * @param {*} point
 * @returns {boolean}
 */
const isLatLngLike = (point) =>
  !!point &&
  typeof point.lat === "function" &&
  typeof point.lng === "function";

/**
 * Extrait le tracé détaillé d'un itinéraire Google Directions.
 *
 * Concatène les `path` de tous les pas de toutes les jambes, dans l'ordre,
 * en éliminant :
 * - les points invalides (absents, sans méthode lat()/lng(), coordonnées
 *   non finies) ;
 * - les doublons consécutifs (les pas d'une même jambe et les jambes d'un
 *   même itinéraire partagent leurs points de raccord).
 *
 * @param {*} route Itinéraire `results.routes[i]` d'une DirectionsResponse.
 * @returns {Array} Tracé détaillé (objets LatLng) ; liste vide si la
 *   réponse est inexploitable (le appelant garde alors son état précédent).
 */
export const extractDetailedTrack = (route) => {
  if (!route || !Array.isArray(route.legs)) {
    return [];
  }

  const track = [];

  const pushPoint = (point) => {
    if (!isLatLngLike(point)) {
      return;
    }
    const lat = point.lat();
    const lng = point.lng();
    if (!Number.isFinite(lat) || !Number.isFinite(lng)) {
      return;
    }
    const previous = track[track.length - 1];
    if (previous && previous.lat() === lat && previous.lng() === lng) {
      return;
    }
    track.push(point);
  };

  route.legs.forEach((leg) => {
    if (!leg || !Array.isArray(leg.steps)) {
      return;
    }
    leg.steps.forEach((step) => {
      if (!step || !Array.isArray(step.path)) {
        return;
      }
      step.path.forEach(pushPoint);
    });
  });

  return track;
};

/**
 * Encode un tracé en polyligne Google (algorithme encoded polyline).
 *
 * Utilise `google.maps.geometry.encoding.encodePath` quand la bibliothèque
 * geometry est chargée ; retourne une chaîne vide sinon (l'appelant peut
 * alors conserver `overview_polyline`).
 *
 * @param {Array} track Tracé issu de `extractDetailedTrack`.
 * @returns {string} Polyligne encodée, ou "" si l'encodage est indisponible.
 */
export const encodeDetailedTrack = (track) => {
  if (!Array.isArray(track) || track.length < 2) {
    return "";
  }
  const encoding =
    typeof window !== "undefined" &&
    window.google &&
    window.google.maps &&
    window.google.maps.geometry &&
    window.google.maps.geometry.encoding;
  if (!encoding || typeof encoding.encodePath !== "function") {
    return "";
  }
  const encoded = encoding.encodePath(track);
  return typeof encoded === "string" ? encoded : "";
};

/**
 * Sérialise le tracé pour la sauvegarde backend : tableau `{lat, lng}`
 * numériques arrondis à la précision de stockage (contrat consommé par le
 * mobile, cf. `Project.fromJson`).
 *
 * @param {Array} track Tracé issu de `extractDetailedTrack` (LatLng) ou de
 *   la base (`{lat, lng}`) — les deux formes sont acceptées.
 * @returns {Array} Liste de `{lat, lng}` numériques arrondis.
 */
export const serializeDetailedTrack = (track) => {
  if (!Array.isArray(track)) {
    return [];
  }
  const points = [];
  track.forEach((point) => {
    if (!point) {
      return;
    }
    const lat = readTrackCoordinate(point, "lat");
    const lng = readTrackCoordinate(point, "lng");
    if (!Number.isFinite(lat) || !Number.isFinite(lng)) {
      return;
    }
    const previous = points[points.length - 1];
    const roundedLat = roundCoordinate(lat);
    const roundedLng = roundCoordinate(lng);
    if (
      previous &&
      previous.lat === roundedLat &&
      previous.lng === roundedLng
    ) {
      return;
    }
    points.push({ lat: roundedLat, lng: roundedLng });
  });
  return points;
};
